<template>
  <div :class="$style.userGrid">
    <template v-if="user">
      <SCADropdown v-model:is-open="isOpenProfileMenu" toolbar-offset width="204px">
        <template #trigger="{ target }">
          <button
            :popovertarget="target"
            :class="$style.block"
            @mouseenter="isHovered = true"
            @mouseleave="isHovered = false"
          >
            <SCAAvatar
              :initials="convertNameToInitials(user.name)"
              size="small"
              :color-idx="user.avatarColorIdx"
              :bordered="!!user.avatarFileUrl"
              :use-hover-colors="isHovered || isOpenProfileMenu"
              :border-color="isProfileAvatarActive ? 'var(--color-border-inverted)' : undefined"
              :border-width="isProfileAvatarActive ? '1.5px' : undefined"
              :class="$style.pointer"
            >
              <img v-if="user.avatarFileUrl" :src="user.avatarFileUrl" />
            </SCAAvatar>
          </button>
        </template>
        <template #default="{ popover }">
          <RouterLink
            key="base-layout-header-module-router-link-profile"
            v-slot="link"
            :to="{ name: Routes.USER_SETTINGS_PROFILE }"
            custom
          >
            <SCADropdownItem
              type="action"
              :label="$t('common.label.profileSettings')"
              icon="phosphor/user-gear"
              icon-size="16px"
              use-smaller-vertical-padding
              use-neutral-icon-fill
              appearance="secondary"
              @click="
                link.navigate();
                popover?.hidePopover();
              "
            />
          </RouterLink>

          <SCADropdown v-model:is-open="isOpenHeaderSettings" width="198px" :offset-x="190" :offset-y="-35" :gap="8">
            <template #trigger="{ target }">
              <button
                type="button"
                :popovertarget="target"
                :class="$style.dropdownTargetButton"
                test-id="header-view-options-trigger"
              >
                <SCADropdownItem
                  :label="$t('common.label.headerViewOptions')"
                  icon-size="16px"
                  use-smaller-vertical-padding
                  use-neutral-icon-fill
                  expandable
                  appearance="secondary"
                  icon="phosphor/browser"
                  :active="isOpenHeaderSettings"
                  :unset-gap="true"
                />
              </button>
            </template>
            <template #default="{ popover: popoverHeaderOptions }">
              <button
                v-for="option in headerViewModeOptions"
                :key="option.mode"
                type="button"
                :class="$style.headerViewModeRow"
                :test-id="`header-view-mode-${option.mode}`"
                @click="
                  layoutStore.setHeaderViewMode(option.mode);
                  popoverHeaderOptions?.hidePopover();
                  popover?.hidePopover();
                "
              >
                <HeaderMenuOption :mode="option.mode" />
                <span
                  :class="[
                    $style.headerViewModeLabel,
                    { [$style.selected]: layoutStore.headerViewMode === option.mode },
                  ]"
                >
                  {{ option.label }}
                </span>
              </button>
            </template>
          </SCADropdown>

          <SCADropdownItem
            type="toggle"
            :checked="layoutStore.breadcrumbsVisibility"
            :label="$t('common.label.breadcrumbs')"
            icon-size="16px"
            use-smaller-vertical-padding
            use-neutral-icon-fill
            unset-gap
            appearance="secondary"
            icon="phosphor/caret-double-right"
            @click="layoutStore.toggleBreadcrumbVisibility"
          />

          <SCADivider appearance="border" :margin="'6px -12px'" />
          <SCADropdownItem
            type="action"
            test-id="email-button"
            :label="$t('common.label.contactSupport')"
            icon="phosphor/mail"
            icon-size="16px"
            :use-smaller-vertical-padding="true"
            use-neutral-icon-fill
            @click="
              email();
              popover?.hidePopover();
            "
          />
          <SCADropdownItem
            type="action"
            :label="$t('common.label.log')"
            test-id="logout-button"
            icon="phosphor/sign-out"
            icon-size="16px"
            :use-smaller-vertical-padding="true"
            use-neutral-icon-fill
            @click="
              logout();
              popover?.hidePopover();
            "
          />
        </template>
      </SCADropdown>
      <div :class="$style.info">
        <SCADropdown
          v-model:is-open="show"
          placement="bottom-end"
          padding-upset
          no-mobile-margin
          :offset-x="-6"
          :offset-y="5"
        >
          <template #trigger="{ target }">
            <div ref="profileContainer" :class="[$style.profileContainer]" :style="{ width: profileWidth + 'px' }">
              <span ref="nameEl" :class="$style.hiddenMeasure">
                {{ user.name }}
              </span>
              <div ref="timeEl" :class="$style.hiddenMeasure">
                {{ headerTime }}
              </div>
              <span :class="$style.name">{{ user.name }}</span>
              <div :class="$style.headerTimeContainer">
                {{ headerTime }}
              </div>
              <button type="button" :popovertarget="target" :aria-label="user.name" :class="$style.triggerButton" />
            </div>
          </template>
          <SCADatetimePicker
            v-model:months="monthsCount"
            v-model:columns="monthColumns"
            inline
            readonly
            :date-format="userDateFormat"
            :time-format="userTimeFormat"
          />
        </SCADropdown>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { SCAAvatar, SCADatetimePicker, SCADivider, SCADropdown, SCADropdownItem } from "@scayla/components";
import { convertNameToInitials, useNow } from "@scayla/core";
import { format } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, useRouter } from "vue-router";

import HeaderMenuOption from "@/components/navigation/HeaderMenuOption.vue";
import { Routes } from "@/router";
import type { HeaderViewMode } from "@/store/layoutStore.ts";
import { useLayoutStore } from "@/store/layoutStore.ts";
import { useUserSettingsStore } from "@/store/userSettings";
import { useWorkspace } from "@/store/workspace";
import { showToastForError } from "@/utils/toast";

const router = useRouter();

const workspace = useWorkspace();
const layoutStore = useLayoutStore();
const userSettingsStore = useUserSettingsStore();
const user = computed(() => workspace?.user);

const show = ref(false);

const isOpenProfileMenu = ref(false);
const isOpenHeaderSettings = ref(false);

const headerViewModeOptions = computed<{ mode: HeaderViewMode; label: string }[]>(() => [
  { mode: "text", label: $t("common.label.text") },
  { mode: "symbol", label: $t("common.label.symbol") },
  { mode: "symbolAndText", label: $t("common.label.symbolAndText") },
]);

const userDateFormat = computed(() => userSettingsStore.dateFormat);
const userTimeFormat = computed(() => userSettingsStore.timeFormat);

const monthsCount = computed({
  get: () => userSettingsStore.dateFieldMonthsCount,
  set: (value) => {
    userSettingsStore.setDateFieldMonthsCount(value).catch(showToastForError);
  },
});
const monthColumns = computed({
  get: () => userSettingsStore.dateFieldMonthColumns,
  set: (value) => {
    userSettingsStore.setDateFieldMonthColumns(value).catch(showToastForError);
  },
});

const { t: $t, locale } = useI18n();
const dateFnsLocale = computed(() => (locale.value.toLowerCase().startsWith("de") ? de : enUS));

const now = useNow();
const headerTime = computed(() => {
  if (!now.value) return "";
  return format(now.value, "EEE dd. MMM. yyyy, HH:mm", { locale: dateFnsLocale.value });
});
const profileContainer = useTemplateRef<HTMLElement>("profileContainer");
const nameEl = ref<HTMLElement>();
const timeEl = ref<HTMLElement>();

const profileWidth = ref<number>(0);
const isHovered = ref(false);

const isProfileAvatarActive = computed(
  () => !!user.value?.avatarFileUrl && (isHovered.value || isOpenProfileMenu.value),
);

const logout = async () => {
  await router.push({ name: Routes.LOGOUT_REDIRECT });
};

const email = () => {
  window.location.href = "mailto:info@scayla.com";
};

onMounted(() => {
  void nextTick();
  updateProfileWidth();
});

function updateProfileWidth() {
  const nameWidth = nameEl.value?.offsetWidth ?? 0;
  const timeWidth = timeEl.value?.offsetWidth ?? 0;
  profileWidth.value = Math.max(nameWidth, timeWidth);
}

watch([() => user.value?.name, () => headerTime.value], () => {
  void nextTick(updateProfileWidth);
});

watch(isOpenProfileMenu, (open) => {
  if (!open) isOpenHeaderSettings.value = false;
});
</script>

<style lang="scss" module>
.userGrid {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
}

.info {
  @include Globals.typo-body-strong-medium;
  line-height: 10px;
  display: flex;
  flex-direction: column;

  .dateNow {
    @include Globals.typo-body-small;
    color: var(--color-content-tertiary-inverted);
  }
}

.headerTimeContainer {
  font-size: 9px;
  line-height: 9px;
  letter-spacing: 0.18px;
  font-weight: 450;
  color: var(--color-interaction-neutral-subtle-active);
  transition: color 0.5s ease-in-out;
}

.profileContainer {
  position: relative;
  display: flex;
  gap: 2px;
  flex-direction: column;
  white-space: nowrap;

  &:hover .name {
    cursor: pointer;
  }

  &:hover .headerTimeContainer {
    cursor: pointer;
  }

  .triggerButton {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
  }
}

.hiddenMeasure {
  position: absolute;
  visibility: hidden;
  white-space: nowrap;
  pointer-events: none;
  white-space: nowrap;
}

.block {
  display: block;
}

.dropdownTargetButton {
  all: unset;
  display: block;
  width: 100%;
  cursor: pointer;
}

.headerViewModeRow {
  all: unset;
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-medium);
  width: 100%;
  cursor: pointer;

  :global(div[class*="headerMenuOptionWrapper"]) {
    opacity: 0.42;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover {
    :global(div[class*="headerMenuOptionWrapper"]) {
      opacity: 1;
    }

    .headerViewModeLabel {
      color: var(--color-content-informative-primary);
    }
  }

  &:has(.headerViewModeLabel.selected) :global(div[class*="headerMenuOptionWrapper"]) {
    opacity: 1;
  }
}

.headerViewModeLabel {
  @include Globals.typo-body-medium;
  line-height: 10.2px;
  color: var(--color-content-disabled);

  &:hover {
    color: var(--color-content-informative-primary);
  }

  &.selected {
    color: var(--color-content-primary);
  }
}

.name {
  line-height: 12px;
  transition:
    color 0.5s ease-in-out,
    opacity 0.5s ease-in-out;
  color: var(--color-content-primary-inverted);
}

.pointer {
  cursor: pointer;
}
</style>
